import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Badge,
  Tooltip,
  Card,
  CardContent,
  IconButton
} from '@mui/material';
import {
  MoneyOff as DebtIcon,
  Warning as WarningIcon,
  Schedule as OverdueIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { getAllDebts } from '../../services/debtService';
import { Debt, DebtStatus } from '../../utils/debtTypes';
import { useNavigate } from 'react-router-dom';

interface DebtNotificationsProps {
  onClose?: () => void;
  maxItems?: number;
}

interface DebtNotification {
  id: string;
  type: 'overdue' | 'high_amount' | 'new_debt';
  title: string;
  message: string;
  debt: Debt;
  priority: 'high' | 'medium' | 'low';
  color: 'error' | 'warning' | 'info';
  icon: React.ReactNode;
}

const DebtNotifications: React.FC<DebtNotificationsProps> = ({ 
  onClose, 
  maxItems = 10 
}) => {
  const [notifications, setNotifications] = useState<DebtNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const navigate = useNavigate();

  const loadDebtNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      // جلب جميع الديون أولاً ثم فلترتها محلياً
      console.log('🔍 جلب جميع الديون للإشعارات...');
      const allDebtsFromDB = await getAllDebts(undefined, 200); // جلب جميع الديون
      console.log(`📊 تم جلب ${allDebtsFromDB.length} دين من قاعدة البيانات`);

      // فلترة الديون النشطة والمسددة جزئياً محلياً
      const allDebts = allDebtsFromDB.filter(debt =>
        debt.status === DebtStatus.ACTIVE || debt.status === DebtStatus.PARTIALLY_PAID
      );
      console.log(`✅ الديون النشطة والمسددة جزئياً: ${allDebts.length}`);

      // حساب إجمالي المبلغ المستحق
      const totalDebtAmount = allDebts.reduce((sum, debt) => sum + (debt.remainingAmount || 0), 0);
      setTotalAmount(totalDebtAmount);

      const debtNotifications: DebtNotification[] = [];
      const now = new Date();
      const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);

      allDebts.forEach(debt => {
        // إشعارات الديون المتأخرة
        if (debt.dueDate && new Date(debt.dueDate) < now) {
          const daysOverdue = Math.floor((now.getTime() - new Date(debt.dueDate).getTime()) / (1000 * 60 * 60 * 24));
          debtNotifications.push({
            id: `overdue-${debt.id}`,
            type: 'overdue',
            title: 'دين متأخر',
            message: `${debt.customerName} - متأخر ${daysOverdue} يوم`,
            debt,
            priority: daysOverdue > 7 ? 'high' : 'medium',
            color: daysOverdue > 7 ? 'error' : 'warning',
            icon: <OverdueIcon />
          });
        }

        // إشعارات الديون القريبة من الاستحقاق
        if (debt.dueDate && new Date(debt.dueDate) > now && new Date(debt.dueDate) <= threeDaysFromNow) {
          const daysUntilDue = Math.ceil((new Date(debt.dueDate).getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          debtNotifications.push({
            id: `due-soon-${debt.id}`,
            type: 'overdue',
            title: 'دين قريب الاستحقاق',
            message: `${debt.customerName} - يستحق خلال ${daysUntilDue} يوم`,
            debt,
            priority: 'medium',
            color: 'warning',
            icon: <WarningIcon />
          });
        }

        // إشعارات الديون عالية القيمة
        if (debt.remainingAmount >= 5000) {
          debtNotifications.push({
            id: `high-amount-${debt.id}`,
            type: 'high_amount',
            title: 'دين عالي القيمة',
            message: `${debt.customerName} - ${debt.remainingAmount.toLocaleString()} ريال متبقي`,
            debt,
            priority: debt.remainingAmount >= 10000 ? 'high' : 'medium',
            color: debt.remainingAmount >= 10000 ? 'error' : 'warning',
            icon: <DebtIcon />
          });
        }

        // إشعارات الديون القريبة من الاستحقاق
        if (debt.dueDate && new Date(debt.dueDate) > now) {
          const daysUntilDue = Math.floor((new Date(debt.dueDate).getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          if (daysUntilDue <= 3) {
            debtNotifications.push({
              id: `due-soon-${debt.id}`,
              type: 'new_debt',
              title: 'دين قريب الاستحقاق',
              message: `${debt.customerName} - يستحق خلال ${daysUntilDue} يوم`,
              debt,
              priority: daysUntilDue <= 1 ? 'high' : 'medium',
              color: daysUntilDue <= 1 ? 'error' : 'warning',
              icon: <WarningIcon />
            });
          }
        }

        // إشعارات الديون الجديدة (آخر 7 أيام)
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        if (debt.createdAt && new Date(debt.createdAt) > sevenDaysAgo) {
          const daysAgo = Math.floor((now.getTime() - new Date(debt.createdAt).getTime()) / (1000 * 60 * 60 * 24));
          debtNotifications.push({
            id: `new-debt-${debt.id}`,
            type: 'new_debt',
            title: 'دين جديد',
            message: `${debt.customerName} - ${debt.amount.toLocaleString()} ج.م (منذ ${daysAgo === 0 ? 'اليوم' : daysAgo + ' يوم'})`,
            debt,
            priority: 'low',
            color: 'info',
            icon: <PersonIcon />
          });
        }
      });

      // ترتيب الإشعارات حسب الأولوية والتاريخ
      const sortedNotifications = debtNotifications
        .sort((a, b) => {
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
            return priorityOrder[b.priority] - priorityOrder[a.priority];
          }
          return new Date(b.debt.createdAt).getTime() - new Date(a.debt.createdAt).getTime();
        })
        .slice(0, maxItems);

      setNotifications(sortedNotifications);
    } catch (err) {
      console.error('Error loading debt notifications:', err);
      setError('فشل في تحميل إشعارات الديون');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDebtNotifications();
  }, [maxItems]);

  const handleViewDebt = (debt: Debt) => {
    navigate('/admin/debts');
    if (onClose) onClose();
  };

  const handleViewAllDebts = () => {
    navigate('/admin/debts');
    if (onClose) onClose();
  };

  const getNotificationCount = () => {
    return notifications.filter(n => n.priority === 'high').length;
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          جاري تحميل الإشعارات...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          onClick={loadDebtNotifications}
          startIcon={<RefreshIcon />}
          fullWidth
        >
          إعادة المحاولة
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 400, maxHeight: 500 }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              💰 إشعارات الديون
            </Typography>
            {totalAmount > 0 && (
              <Typography variant="caption" color="text.secondary">
                إجمالي المستحق: {totalAmount.toLocaleString()} ريال
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="تحديث">
              <IconButton size="small" onClick={loadDebtNotifications}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {getNotificationCount() > 0 && (
              <Badge badgeContent={getNotificationCount()} color="error" />
            )}
          </Box>
        </Box>

        {/* إحصائيات سريعة */}
        {notifications.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={`${notifications.filter(n => n.type === 'overdue').length} متأخر`}
              color="error"
              variant="outlined"
            />
            <Chip
              size="small"
              label={`${notifications.filter(n => n.type === 'high_amount').length} عالي القيمة`}
              color="warning"
              variant="outlined"
            />
            <Chip
              size="small"
              label={`${notifications.filter(n => n.priority === 'high').length} عاجل`}
              color="error"
              variant="filled"
            />
          </Box>
        )}
      </Box>

      {/* Notifications List */}
      <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
        {notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <DebtIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              لا توجد إشعارات ديون جديدة
            </Typography>
            <Typography variant="body2" color="text.secondary">
              جميع الديون تحت السيطرة! 👍
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  sx={{
                    py: 1.5,
                    px: 2,
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                  onClick={() => handleViewDebt(notification.debt)}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: '50%',
                        bgcolor: `${notification.color}.light`,
                        color: `${notification.color}.main`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {notification.icon}
                    </Box>
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {notification.title}
                        </Typography>
                        <Chip
                          label={notification.priority === 'high' ? 'عاجل' : notification.priority === 'medium' ? 'مهم' : 'عادي'}
                          size="small"
                          color={notification.color}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {notification.message}
                      </Typography>
                    }
                  />
                  
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <ViewIcon fontSize="small" />
                  </IconButton>
                </ListItem>
                
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* Footer */}
      {notifications.length > 0 && (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleViewAllDebts}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold'
            }}
          >
            عرض جميع الديون ({notifications.length})
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default DebtNotifications;
